import {
  ref, computed, watch,
} from 'vue';
import isIframe from './isIframe';

/**
 * This function provides embed code related controls
 * @param {object} params This object contain two parameter.
 * @param {object} params.model Component's lazyValue
 * @param {string} params.field The field to be monitored in the model
 * @returns {object} object
 * @example{
 *   const model = {type: "", path: "", code: "anyEmbedCode", width: 640, height: 580, duration: null};
 *
 *   const fieldToWatch = "code";
 *
 *   const {
 *     isLazyEmbedEntered, isEmbedIframe, showEmbedInput, getEmbedProperties,
 *   } = useEmbed({ model, field});
 *
 *   return{
 *     isLazyEmbedEntered: Provides information whether there is an embedCode or not   // true | false,
 *     isEmbedIframe: Provides information whether the value entered is suitable embed code pattern or not   // true | false,
 *     showEmbedInput: Provides information whether to show embed code input field or not   // true | false,
 *     getEmbedProperties: Returns embed code property  // width, height
 *   }
 * }
 */
const useEmbed = ({ model, field }) => {
  const embedCode = ref(null);
  const loadedScripts = [];

  watch(() => model.value[field] && model.value[field], newValue => {
    embedCode.value = newValue;
  });

  const isLazyEmbedEntered = computed(() => !!embedCode.value);

  const isEmbedIframe = computed(() => isIframe(embedCode.value)
    || /(?:<blockquote[^>]*)(?:(?:\/>)|(?:>.*?<\/blockquote>))/.test(embedCode.value)
    || /(?:<script[^>]*)(?:(?:\/>)|(?:>.*?<\/script>))/.test(embedCode.value));
  const showEmbedInput = computed(() => !(embedCode.value && isEmbedIframe.value));

  const getEmbedProperties = () => {
    const properties = {};

    embedCode.value.replace(/(width|height)=["']([^"']*)["']/gi, (all, type, value) => {
      properties[type] = parseInt(value, 10);
    });

    return properties;
  };

  const loadEmbedScript = (scriptId, scriptSrc) => {
    const prevScript = document.getElementById(scriptId);

    if (prevScript) {
      prevScript.remove();
    }

    if (scriptSrc) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = scriptId;
      script.src = scriptSrc;
      script.async = true;
      script.addEventListener('load', () => {
        if (window.instgrm) {
          window.instgrm.Embeds.process();
        }
      });
      document.body.insertAdjacentElement('afterend', script);
      loadedScripts.push(script);
    }
  };

  const removeEmbedScripts = (scriptsArray = loadedScripts) => {
    let removedScriptNumber = 0;
    scriptsArray.forEach((script, index) => {
      script.remove();
      removedScriptNumber = index;
    });

    scriptsArray.splice(removedScriptNumber, removedScriptNumber + 1);
  };

  return {
    isLazyEmbedEntered,
    isEmbedIframe,
    showEmbedInput,
    getEmbedProperties,
    loadEmbedScript,
    removeEmbedScripts,
  };
};

export default useEmbed;
